<template>
  <form-section>
    <el-row>
      <el-col :span="10">
        <form-item :label="$t('Sites_Form_Page_Title_Field_Name')" prop="name">
          <el-input
            v-model="value.name"
            :maxlength="100"
            data-qa="site-page-title-input"
          />
        </form-item>
        <form-item :label="$t('Sites_Form_Page_Url_Field_Name')" prop="slug">
          <div style="display: flex">
            <el-input
              v-model="value.slug"
              class="domain-input"
              data-qa="site-page-url-input"
            >
              <template slot="prepend">
                <span class="domain" :disabled="!fullDomain">
                  {{ fullDomain ? fullDomain + '/' : 'https://xxxxxxxxxxxx/' }}
                </span>
              </template>
            </el-input>
            <el-tooltip
              :open-delay="300"
              :content="$t('Sites_Form_Page_Url_Tooltip')"
              placement="top"
            >
              <el-button
                type="primary"
                plain
                size="small"
                class="btn"
                @click="openUrl"
                :disabled="!(fullDomain && value.slug && value.id)"
              >
                <icon
                  glyph="share"
                  :style="
                    !(fullDomain && value.slug && value.id) && {
                      color: 'lightgray'
                    }
                  "
                />
              </el-button>
            </el-tooltip>
            <el-tooltip
              :open-delay="300"
              :content="$t('Sites_Form_Page_Token_Tooltip')"
              placement="top"
            >
              <el-button
                type="primary"
                plain
                size="small"
                class="btn"
                @click="copyToClipboard"
                :disabled="!value.id"
              >
                <icon
                  glyph="angle-brackets"
                  :style="
                    !value.id && {
                      color: 'lightgray'
                    }
                  "
                />
              </el-button>
            </el-tooltip>
          </div>
        </form-item>
        <template v-if="hasForm">
          <form-item
            :label="$t('Sites_Form_Page_Url_Field_ReturnPage')"
            prop="return_page_uuid"
          >
            <el-select v-model="value.return_page_uuid">
              <el-option
                v-if="page.uuid"
                v-for="page in pages"
                :key="page.uuid"
                :label="page.name"
                :value="page.uuid"
              />
              <el-option
                :value="null"
                :label="$t('Sites_Form_Page_Url_Option_CustomUrl')"
                key="return_custom_url"
              />
            </el-select>
          </form-item>
          <form-item
            v-if="value.return_type === ReturnTypes.CUSTOM_URL"
            :label="$t('Sites_Form_Page_Url_Field_ReturnUrl')"
            prop="return_url"
          >
            <el-input v-model="value.return_url" :maxlength="255" />
          </form-item>
        </template>
      </el-col>
      <el-col :span="11" :offset="1">
        <div class="page-analytics-ct">
          <div class="analytics-item">
            <h5 class="analytics-label">
              {{ $t('Sites_Form_Page_Visitors') }}
            </h5>
            <p class="analytics-metric">
              {{ formatNumber(value.hit_count) }}
            </p>
          </div>
          <div v-if="hasForm" class="analytics-item">
            <h5 class="analytics-label">
              {{ $t('Sites_Form_Page_FormSubmissions') }}
            </h5>
            <p class="analytics-metric">
              {{ formatNumber(value.form_submission_count) }}
            </p>
          </div>
        </div>
        <div class="preview">
          <iframe
            v-if="pagePreview"
            :srcdoc="pagePreview"
            class="page-preview"
          />
        </div>
        <div class="edit-btn-ct">
          <el-button
            type="primary"
            @click="showBuilder = true"
            :disabled="readonly"
            data-qa="edit-page-content-button"
          >
            {{ $t('Sites_Form_Page_Button_Edit') }}
          </el-button>
        </div>
      </el-col>
    </el-row>
    <page-builder
      v-if="showBuilder && !readonly"
      :on-close="() => (showBuilder = false)"
      v-model="value"
    />
  </form-section>
</template>

<script>
import FormItem from '@/common/components/form/FormItem'
import FormSection from '@/common/components/form/FormSection'
import PageBuilder from '@/modules/sites/components/PageBuilder'
import i18n from '@/core/utils/I18n'
import { ReturnTypes } from '@/modules/sites/enum/ReturnTypesEnum'
export default {
  name: 'Page',
  components: { PageBuilder, FormSection, FormItem },

  props: {
    readonly: {
      default: false,
      required: false
    },
    value: {
      type: Object,
      required: true
    },
    pages: {
      type: Array,
      required: true
    },
    domain: {
      type: String,
      required: false
    },
    subdomain: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      ReturnTypes,
      showBuilder: false,
      regex: /<form[^>]*>/
    }
  },
  computed: {
    pagePreview() {
      return this.value.page_html
    },
    updateSlug() {
      return true
    },
    fullDomain() {
      return this.domain
        ? this.domain.replace('{subdomain}', this.subdomain || 'xxxxxxxxxx')
        : ''
    },
    hasForm() {
      return this.value.page_html && this.regex.test(this.value.page_html)
    }
  },
  methods: {
    copyToClipboard() {
      navigator.clipboard
        .writeText(`{{site('${this.value.id}')}}`)
        .then(_ =>
          this.$message({
            message: this.$t(`Sites_Form_Page_Token_CopiedSuccessfully`),
            type: 'success'
          })
        )
        .catch(_ =>
          this.$message.error(
            this.$t(`Sites_Form_Page_Token_CopiedUnsuccessfully`)
          )
        )
    },
    openUrl() {
      window.open(`${this.fullDomain}/${this.value.slug}`, '_blank')
    },
    formatNumber(value) {
      if (isNaN(value)) {
        return 0
      }

      return new Intl.NumberFormat(i18n.locale, {
        notation: 'compact'
      }).format(value)
    }
  },
  watch: {
    'value.return_page_uuid': {
      handler() {
        if (this.value.return_page_uuid) {
          this.value.return_type = ReturnTypes.PAGE
        } else if (this.value.return_page_uuid === null) {
          this.value.return_type = ReturnTypes.CUSTOM_URL
        }
      }
    },
    'value.name': function(newName) {
      if (this.updateSlug) {
        this.value.slug = newName
          .trim()
          .replace(/[^A-Za-z0-9\-\s]/g, '')
          .replace(/[A-Z]/g, function(matchChar) {
            return matchChar.toLowerCase()
          })
          .replace(/\s+/g, '-')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.el-icon-document-copy {
  cursor: pointer;
}

.domain-input {
  >>> .el-input-group__prepend {
    width: 60%;
  }
}

.btn {
  padding: 9px 11px;
  margin-left: 5px;

  >>> span {
    font-size: 16px;
    color: #1d87c8;
  }
}
.preview {
  overflow: unset;
  background-color: #f8fafc;
  border-radius: 4px;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  .preview-btn {
    position: absolute;
    top: 20px;
    right: 40px;
    button {
      color: #1d87c9;
    }
  }
  .page-preview {
    position: absolute;
    background-color: #ffffff;
    width: 200%;
    max-width: 200%;
    height: 200%;
    max-height: 200%;
    border: 0;
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1));
    margin: 0;
    box-sizing: border-box;
    pointer-events: none;
    transform: scale(0.5);
  }
}
.edit-btn-ct {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.page-analytics-ct {
  display: flex;
  margin-bottom: 20px;

  .analytics-item {
    display: flex;
    flex-direction: column;
    margin-right: 100px;
  }

  .analytics-label {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    color: #808080;
  }

  .analytics-metric {
    font-weight: 600;
    font-size: 16px;
    line-height: 30px;
    color: #333333;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/sites/components/Page.vue